/**
 * The project's Quality tab (vfs-service src/quality, in the person's sandbox pod): tests and coverage, lint, secrets,
 * security rules, vulnerabilities (OSV), dependencies and licences, the SBOM, and the release gate that can block
 * Publish. Same base URL, authorized fetch and ACL headers as the Data and Traces tabs (DataService.js), and their
 * errors: a 404 without a JSON body means the sandbox runs an older vfs-service (`code: 'sandbox_outdated'`).
 * GETs need a viewer, writes an editor, the gate override an owner (403 `owner_only`); the server checks.
 *
 * Shapes: the contract "Flow Quality tab: API contract (v1)" (Overview, CheckResult, Gate, Settings, RunSummary,
 * Detected).
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { buildAclContextHeaders } from '../utils/aclContext';
import { DataError, fail } from './DataService';

const base = () => (config.vfsServiceUrl || '').replace(/\/$/, '');
const qualityUrl = (projectId, path = '') => `${base()}/api/v1/projects/${encodeURIComponent(projectId)}/quality${path}`;
const seg = (value) => encodeURIComponent(String(value));

/** The window event the panel and the Publish dialog send after a run, an override or a settings save. */
export const QUALITY_CHANGED_EVENT = 'flow:quality-changed';

export function notifyQualityChanged(projectId) {
  if (typeof window !== 'undefined') { window.dispatchEvent(new CustomEvent(QUALITY_CHANGED_EVENT, { detail: { projectId } })); }
}

const query = (params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '' && value !== false) { search.set(key, value === true ? '1' : String(value)); }
  });
  const text = search.toString();
  return text ? `?${text}` : '';
};

async function send(url, { method = 'GET', body } = {}) {
  if (!base()) { throw new DataError('vfs-service is not configured', 0, null); }
  const json = body !== undefined;
  return getAuthorizedFetch()(url, {
    method,
    headers: { Accept: 'application/json', ...buildAclContextHeaders(), ...(json ? { 'Content-Type': 'application/json' } : {}) },
    ...(json ? { body: JSON.stringify(body) } : {}),
  });
}

async function call(url, options) {
  const response = await send(url, options);
  if (!response.ok) { throw await fail(response); }
  if (response.status === 204) { return {}; }
  return (await response.json().catch(() => null)) || {};
}

/**
 * Every check, the gate, the history and what this person may do: `Overview`. `service`: that service's checks only
 * (the tab's "All services / <service>" filter; the gate stays the project's). `deploy.off`: the services not deployed.
 */
export function getOverview(projectId, { service } = {}) {
  return call(`${qualityUrl(projectId)}${query({ service })}`);
}

/**
 * Starts a run: `{ runId, checks }` (202). `checks` (default all), `trigger`: 'manual' | 'publish', `services`,
 * `failedOnly` (tests: only the failing files). A DataError 409 `busy` (with `body.runId`) when one already runs.
 */
export function startRun(projectId, { checks, trigger, services, failedOnly } = {}) {
  const body = {};
  if (checks?.length) { body.checks = checks; }
  if (trigger) { body.trigger = trigger; }
  if (services?.length) { body.services = services; }
  if (failedOnly) { body.failedOnly = true; }
  return call(qualityUrl(projectId, '/runs'), { method: 'POST', body });
}

/**
 * "Start and test" / "Start all and run all checks": starts the services whose tests cannot run yet (never started since
 * the sandbox woke; with `checks: 'all'` every stopped one), waits for their dependencies, then runs their tests (or
 * every check). `{ starting }` (202); the overview's `starting` says where it is. DataError 409 `busy`, 400
 * `nothing_to_start` (every service is ready: run the checks).
 */
export function startAndTest(projectId, { services, checks } = {}) {
  const body = {};
  if (services?.length) { body.services = services; }
  if (checks) { body.checks = checks; }
  return call(qualityUrl(projectId, '/start-and-test'), { method: 'POST', body });
}

/** Stops a "Start and test" that waits for its services (Stop all): it starts no run. `{ cancelled }`. */
export function cancelStartAndTest(projectId) {
  return call(qualityUrl(projectId, '/start-and-test/cancel'), { method: 'POST', body: {} });
}

/** Stops the run in progress: `{ cancelled }`. */
export function cancelRun(projectId) {
  return call(qualityUrl(projectId, '/runs/current/cancel'), { method: 'POST', body: {} });
}

/** The run history, newest first: `{ runs: RunSummary[] }`. */
export function listRuns(projectId) {
  return call(qualityUrl(projectId, '/runs'));
}

/** One check of the last run, its findings filtered: `CheckResult`; 404 `not_run`. */
export function getCheck(projectId, checkId, { service, severity, q, includeIgnored, limit } = {}) {
  return call(qualityUrl(projectId, `/checks/${seg(checkId)}${query({ service, severity, q: q ? q.trim() : undefined, includeIgnored, limit })}`));
}

/** `{ settings, source: 'file' | 'default' | 'invalid', errors?, path, canEdit }`. */
export function getSettings(projectId) {
  return call(qualityUrl(projectId, '/settings'));
}

/** Saves the whole settings: `{ settings, source }`; 400 `invalid` with `body.errors: [{ path, message }]`. */
export function saveSettings(projectId, settings) {
  return call(qualityUrl(projectId, '/settings'), { method: 'PUT', body: { settings } });
}

/** Writes the recommended set: `{ settings, source: 'file' }`. */
export function applyRecommended(projectId) {
  return call(qualityUrl(projectId, '/settings/recommended'), { method: 'POST', body: {} });
}

/** Adds an ignore rule `{ what, check?, path?, reason, until }`: `{ settings }` (201); 400 `invalid`. */
export function addIgnore(projectId, rule) {
  const body = { what: rule.what, reason: rule.reason, until: rule.until ?? null };
  if (rule.check) { body.check = rule.check; }
  if (rule.path) { body.path = rule.path; }
  return call(qualityUrl(projectId, '/ignores'), { method: 'POST', body });
}

/** Removes the ignore rule at `index` (its place in `settings.ignore`): `{ settings }`. */
export function deleteIgnore(projectId, index) {
  return call(qualityUrl(projectId, `/ignores/${seg(index)}`), { method: 'DELETE' });
}

/** What the Publish dialog reads: `{ gate, running, settings: { when } }`. */
export function getGate(projectId) {
  return call(qualityUrl(projectId, '/gate'));
}

/** Publish past a blocked gate (owners), with a reason of 10..1000 characters: `{ override, gate }`. */
export function overrideGate(projectId, reason) {
  return call(qualityUrl(projectId, '/gate/override'), { method: 'POST', body: { reason } });
}

/** The overrides, newest first: `{ overrides }`. */
export function getEvidence(projectId) {
  return call(qualityUrl(projectId, '/evidence'));
}

/** The languages, test and lint tools found in the code: `Detected`. */
export function detect(projectId) {
  return call(qualityUrl(projectId, '/detect'));
}

/** The file's name the server gives (Content-Disposition), else `fallback`. */
function fileNameOf(response, fallback) {
  const header = response.headers?.get?.('Content-Disposition') || '';
  const match = /filename="?([^";]+)"?/i.exec(header);
  return match ? match[1] : fallback;
}

/** Saves a fetched answer as a file (the browser needs the auth headers, so this is a fetch and a blob, not a link). */
async function saveAs(response, fileName) {
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

/**
 * The SBOM as CycloneDX JSON, to read rather than save (the Dependencies page's package list): `{ components: [{ name,
 * version, group?, purl?, licenses?, scope: 'required'|'excluded' (dev), properties: [{ name: 'flow:services' |
 * 'flow:lockfile' | 'flow:licenceSource' | 'flow:direct', value }] }] }`. Made from the lockfiles as they are now.
 */
export function getSbom(projectId) {
  return call(qualityUrl(projectId, `/sbom${query({ format: 'cyclonedx' })}`));
}

/** Downloads the SBOM (`format`: 'cyclonedx' | 'spdx') as a file, as PublishService.downloadZip does. */
export async function downloadSbom(projectId, format = 'cyclonedx') {
  const response = await send(qualityUrl(projectId, `/sbom${query({ format })}`), { method: 'GET' });
  if (!response.ok) { throw await fail(response); }
  await saveAs(response, fileNameOf(response, `${projectId}-sbom.${format === 'spdx' ? 'spdx' : 'cdx'}.json`));
}

/**
 * "Create lockfiles": the sandbox runs each service's own package manager (npm, pnpm, Yarn, uv, poetry, pipenv, pip,
 * go, cargo, dotnet, gradle), lockfile only, for every service whose manifest has none (or only `services`), saves each
 * lockfile into the project as you, then runs the dependency checks again (`run`): `{ results: [{ service, manifest?,
 * status: 'created'|'exists'|'not_ready'|'tool_missing'|'unsupported'|'error'|'nothing_to_lock', message, file?, tool?,
 * missingTool?, output?, network? }], created, refreshed, run }`.
 *
 * `start`: the services that need a lockfile and were never started since the sandbox woke are started first, in the
 * background: `{ lockfileJob }` (202), the overview's `lockfileJob` says where it is (`starting`, `creating`) and, once
 * `done`, the same `results`, `created`, `refreshed`; none to start (or an older sandbox): the answer above at once.
 */
export function createLockfiles(projectId, services, { start = false } = {}) {
  const body = services?.length ? { services } : {};
  if (start) { body.start = true; }
  return call(qualityUrl(projectId, '/lockfiles'), { method: 'POST', body });
}

/** Stops a "Create lockfiles" that starts the services first (the Stop button, Stop all). `{ cancelled }`. */
export function cancelLockfiles(projectId) {
  return call(qualityUrl(projectId, '/lockfiles/cancel'), { method: 'POST', body: {} });
}

/** The running web previews the accessibility check can open: `{ targets: [{ service, url }], skipped: [{ service, reason }] }`. */
export function getAccessibilityTargets(projectId) {
  return call(qualityUrl(projectId, '/accessibility/targets'));
}

/** Sends what axe-core found in this browser (a11yRunner.js): `{ check, gate }` (201). */
export function postAccessibilityResults(projectId, result) {
  return call(qualityUrl(projectId, '/accessibility/results'), { method: 'POST', body: result });
}

/** After a successful Publish: the API contract of each service is kept, to compare the next changes with. */
export function snapshotApiContract(projectId) {
  return call(qualityUrl(projectId, '/api-contract/snapshot'), { method: 'POST', body: {} });
}

// v3: build provenance (a signed in-toto statement per release) and the compliance mapping (ASVS, ISO 27001 Annex A).
// These are not checks: the engine never runs them, the server computes them when read.

/** The releases recorded, newest first: `{ provenance: Summary[], key: Key, gateRule }`. */
export function getProvenance(projectId) {
  return call(qualityUrl(projectId, '/provenance'));
}

/** One release: `{ provenance: Summary, statement, envelope, verify: Verify }`; 404 `not_found`. */
export function getProvenanceItem(projectId, id) {
  return call(qualityUrl(projectId, `/provenance/${seg(id)}`));
}

/** The current signing key: `{ keyid, algorithm, publicKeyPem, source, version }`. */
export function provenanceKey(projectId) {
  return call(qualityUrl(projectId, '/provenance/key'));
}

/** Downloads a release's attestation (the DSSE envelope, one JSON line) as `<slug>-<id>.intoto.jsonl`. */
export async function downloadAttestation(projectId, id) {
  const response = await send(qualityUrl(projectId, `/provenance/${seg(id)}/attestation`), { method: 'GET' });
  if (!response.ok) { throw await fail(response); }
  await saveAs(response, fileNameOf(response, `${projectId}-${id}.intoto.jsonl`));
}

/**
 * After a successful delivery: records the release's signed provenance. `destination: { kind: 'console'|'git'|'zip',
 * target?, url?, environment? }`, `releaseId` (the `X-Flow-Release-Id` of the zip, when readable), `images` (Console's
 * digests, when reported). `{ provenance: Summary }` (201).
 */
export function recordProvenance(projectId, { destination, releaseId, images } = {}) {
  const body = { destination };
  if (releaseId) { body.releaseId = releaseId; }
  if (images?.length) { body.images = images; }
  return call(qualityUrl(projectId, '/provenance'), { method: 'POST', body });
}

/** The evidence for each control of the frameworks: `Compliance`. */
export function getCompliance(projectId) {
  return call(qualityUrl(projectId, '/compliance'));
}

/**
 * The compliance report. `format: 'json'` downloads `<slug>-compliance.json`; `'html'` resolves to the printable page's
 * HTML text (the caller opens it, e.g. in a new window through a blob URL).
 */
export async function complianceReport(projectId, format = 'json') {
  const response = await send(qualityUrl(projectId, `/compliance/report${query({ format })}`), { method: 'GET' });
  if (!response.ok) { throw await fail(response); }
  if (format === 'html') { return response.text(); }
  await saveAs(response, fileNameOf(response, `${projectId}-compliance.json`));
  return null;
}

// Compliance assessments (GDPR, NIS2, DORA, EU AI Act): controls with automatic checks and the team's answers.
// Evidence for an assessment, never a certification. Opt-in per project (`settings.assessments`).

/** The four assessments, summarised, with the settings: `{ disclaimer, canEdit, settings, frameworks }`. */
export function getAssessments(projectId) {
  return call(qualityUrl(projectId, '/assessments'));
}

/** One assessment with its controls, items, evidence and fixes; 404 `not_found` for an unknown one. */
export function getAssessment(projectId, framework) {
  return call(qualityUrl(projectId, `/assessments/${seg(framework)}`));
}

/** Records answers (`[{ question, value, note? }]`, `value: null` clears one); resolves to the assessment now. */
export function saveAssessmentAnswers(projectId, framework, answers, summary = null) {
  return call(qualityUrl(projectId, `/assessments/${seg(framework)}/answers`), { method: 'PUT', body: { answers, ...(summary ? { summary } : {}) } });
}

/** Turns assessments on, into the release gate, or `blockUnanswered`: `{ saved, settings }`. */
export function saveAssessmentSettings(projectId, settings) {
  return call(qualityUrl(projectId, '/assessments/settings'), { method: 'PUT', body: settings });
}

/** The report of one assessment: `'json'` downloads it; `'html'` resolves to the printable page's HTML text. */
export async function assessmentReport(projectId, framework, format = 'json') {
  const response = await send(qualityUrl(projectId, `/assessments/${seg(framework)}/report${query({ format })}`), { method: 'GET' });
  if (!response.ok) { throw await fail(response); }
  if (format === 'html') { return response.text(); }
  await saveAs(response, fileNameOf(response, `${projectId}-${framework}-assessment.json`));
  return null;
}

// v3 Tests and coverage drill-down.

/** One run in detail (`'latest'`: the newest with test detail): `RunDetail`; 404 `not_found`. */
export function getRunDetail(projectId, runId = 'latest') {
  return call(qualityUrl(projectId, `/runs/${seg(runId)}`));
}

/** Coverage per service and file, and its trend: `{ runId, label, services, trend }`; 404 `not_run`. */
export function getCoverage(projectId, { runId } = {}) {
  return call(qualityUrl(projectId, `/coverage${query({ runId })}`));
}

/** One file's line map and source: `{ path, service, runId, metrics, source, lines, uncoveredRanges, changedSinceRun }`. */
export function getCoverageFile(projectId, { path, runId } = {}) {
  return call(qualityUrl(projectId, `/coverage/file${query({ path, runId })}`));
}

/** "Add coverage": the coverage tool per service: `{ results: [{ service, status, message, file? }] }`. */
export function setupCoverage(projectId, services) {
  return call(qualityUrl(projectId, '/coverage/setup'), { method: 'POST', body: services?.length ? { services } : {} });
}

// External tools: SonarQube / SonarCloud and the Semgrep AppSec Platform (configured by the Flow admin, opted in per
// project in the settings' `external`), and SARIF files imported from any other scanner. The export is SARIF 2.1.0.

/** What the organization configured, this project's opt-in and the imports: `{ org, project, imports }`. */
export function getExternal(projectId) {
  return call(qualityUrl(projectId, '/external'));
}

/**
 * Imports a SARIF file (editors): `source` ('snyk'|'github'|'gitlab'|'defectdojo'|'sonar'|'semgrep'|'other'), `name`
 * (optional), `sarif` (the file's text or the parsed object, up to 10 MB). `{ import, check, gate }` (201); 400
 * `invalid` with `body.errors`.
 */
export function importSarif(projectId, { source, name, sarif }) {
  const body = { source, sarif };
  if (name && name.trim()) { body.name = name.trim(); }
  return call(qualityUrl(projectId, '/external/sarif'), { method: 'POST', body });
}

/** Removes an imported SARIF file (editors): `{ removed, check, gate }`. */
export function deleteSarifImport(projectId, importId) {
  return call(qualityUrl(projectId, `/external/sarif/${seg(importId)}`), { method: 'DELETE' });
}

/** Downloads the current findings (not ignored) as SARIF 2.1.0 (`checks`: only those), as the SBOM is downloaded. */
export async function downloadSarif(projectId, checks = null) {
  const response = await send(qualityUrl(projectId, `/sarif${query({ checks: checks?.length ? checks.join(',') : undefined })}`), { method: 'GET' });
  if (!response.ok) { throw await fail(response); }
  await saveAs(response, fileNameOf(response, `${projectId}-quality.sarif`));
}
